import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { BlockerWrapper } from "components/BlockerWrapper/BlockerWrapper"
import { DisplayPreferencesProvider } from "components/DisplayPreferences/DisplayPreferencesProvider"
import { ErrorBoundary } from "components/ErrorBoundary/ErrorBoundary"
import { LocaleProvider } from "components/LocaleProvider/LocaleProvider"
import { PropsWithChildren } from "react"
import { AuthProvider } from "store/auth/AuthContext"
import { TrackingWrapper } from "tracking/TrackingWrapper"

import { Locale } from "@shared/enums/general"

export interface AppWrapperProps extends PropsWithChildren {
  /**
   *  Our application is tightly integrated with the React Query library.
   *  This is the instance of the query client that should be used across
   *  the app.
   */
  queryClient: QueryClient

  /**
   *  An optional locale to use across the app. This option should only
   *  be used in environments where the locale is supposed to be static.
   *  This means tests or storybook. During normal operation, the app should
   *  deduce the locale based on the browser's language settings.
   */
  locale?: Locale
}

/**
 *  This is a non-visual component that is tasked with wrapping the application
 *  in all the necessary providers that are required for the app to run.
 *
 *  This wrapper should be used across all environments the application runs in.
 *  This includes unit tests, storybook, and the production environment.
 */
export function AppWrapper({ children, queryClient, locale }: AppWrapperProps) {
  return (
    <BlockerWrapper>
      <TrackingWrapper>
        <DisplayPreferencesProvider>
          <div className="App">
            <QueryClientProvider client={queryClient}>
              <AuthProvider>
                <LocaleProvider locale={locale}>
                  <ErrorBoundary>{children}</ErrorBoundary>
                </LocaleProvider>
              </AuthProvider>
            </QueryClientProvider>
          </div>
        </DisplayPreferencesProvider>
      </TrackingWrapper>
    </BlockerWrapper>
  )
}
